import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { getPlayerImageUrl } from "../../../helper/helper";
import type { IMatchDetails, IPlayersTeamSquad } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import type { CommVm, Inn, Phase, TeamInfo } from "./derive";
import { initials } from "./derive";

export type TabKey = "summary" | "scorecard" | "commentary" | "players" | "analysis";

export type Ctx = {
  match: IMatchDetails;
  teams: [TeamInfo, TeamInfo];
  inns: Inn[];
  comms: (CommVm | null)[];
  commLoading: boolean;
  /** Test / multi-day match (no chase target, axes sized to overs bowled) */
  multiDay: boolean;
  /** format phases (T20 powerplay, 50-over P1/P2/P3); empty for Test / multi-day */
  phases: Phase[];
  /** ball-by-ball commentary (loaded on demand for the Commentary tab) */
  fullComms: (CommVm | null)[];
  fullLoading: boolean;
  squads: [IPlayersTeamSquad | undefined, IPlayersTeamSquad | undefined];
  winner: TeamInfo | null;
  potmId?: string;
  potmName: string;
  openPlayer: (id?: string | null) => void;
  go: (tab: TabKey) => void;
};

export const Crest = ({ team, className = "crest" }: { team: TeamInfo; className?: string }) => {
  const src = getPlayerImageUrl(team.image);
  return (
    <span className={className} style={{ background: team.color, position: "relative", overflow: "hidden", borderRadius: "50%" }}>
      {team.code}
      {src ? <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
    </span>
  );
};

export const Avatar = ({ name, image, color, soft, className = "av" }: { name: string; image?: string | null; color?: string; soft?: string; className?: string }) => {
  const src = getPlayerImageUrl(image);
  const [bad, setBad] = useState(false);
  return (
    <span className={className} style={soft ? { background: soft, color } : undefined}>
      {src && !bad ? <img src={src} alt="" onError={() => setBad(true)} /> : initials(name)}
    </span>
  );
};

export const NameLink = ({ id, onOpen, children }: { id?: string | null; onOpen: (id?: string | null) => void; children: ReactNode }) =>
  id ? (
    <button type="button" className="nmlink" onClick={() => onOpen(id)}>
      {children}
    </button>
  ) : (
    <>{children}</>
  );

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;

export type SegOpt = { key: string; label: ReactNode };
export const Seg = ({ opts, cur, onPick, label }: { opts: SegOpt[]; cur: string; onPick: (k: string) => void; label: string }) => (
  <div className="seg" role="group" aria-label={label}>
    {opts.map((o) => (
      <button key={o.key} type="button" aria-pressed={o.key === cur} onClick={() => onPick(o.key)}>
        {o.label}
      </button>
    ))}
  </div>
);

/** Pixel width of a chart's container, so SVG charts are drawn 1:1 (real text size) instead of being scaled down on phones. */
export const useBoxWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(Math.round(el.clientWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
};
